/**
 * Zakładki produktowe Sleeka.
 *
 * Pasek zakładek powstaje TUTAJ, z paneli — sekcja nie widzi ustawień swoich
 * bloków (`section.blocks[i].settings` jest puste, a `type` zwraca `@theme`),
 * więc w Liquidzie nie ma z czego zbudować przycisków. Każdy panel niesie
 * własny tytuł w `data-tab-title` i to wystarczy.
 *
 * Bez JS-a pasek zostaje pusty i schowany, a wszystkie panele są widoczne —
 * treść nigdy nie znika. Sleek degraduje się tak samo
 * (`.no-js .tabs__nav{display:none}` + `.no-js .tabs__content{display:block}`).
 *
 * Wygląd: assets/sleek-tabs.css.
 */

class SleekTabs extends HTMLElement {
  /** @type {HTMLElement[]} */ #panels = [];
  /** @type {HTMLButtonElement[]} */ #tabs = [];
  #active = -1;

  connectedCallback() {
    this.nav = this.querySelector('.sleek-tabs__nav');
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.sleek-tabs__panel')]);

    // Jedna zakładka to nie zakładki — zostaje sama siatka produktów.
    if (!this.nav || this.#panels.length < 2) {
      this.setAttribute('ready', '');
      return;
    }

    this.#build();
    this.select(0);
    this.setAttribute('ready', '');

    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  #build() {
    const base = this.id || `sleek-tabs-${Math.random().toString(36).slice(2, 8)}`;

    this.#tabs = this.#panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'sleek-tabs__tab';
      tab.id = `${base}-tab-${index}`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      // Poza aktywną zakładką wyjmujemy przyciski z kolejności tabulacji —
      // wzorzec ARIA dla zakładek: Tab wchodzi w pasek, strzałki chodzą po nim.
      tab.tabIndex = -1;
      tab.textContent = panel.dataset.tabTitle || '';

      panel.id = panel.id || `${base}-panel-${index}`;
      panel.setAttribute('aria-labelledby', tab.id);
      tab.setAttribute('aria-controls', panel.id);

      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));

      return tab;
    });

    this.nav.append(...this.#tabs);
  }

  /** Pokazuje panel o podanym numerze. */
  select(index) {
    if (index === this.#active) return;
    this.#active = index;

    this.#tabs.forEach((tab, i) => {
      const current = i === index;
      tab.setAttribute('aria-selected', current ? 'true' : 'false');
      tab.tabIndex = current ? 0 : -1;
    });

    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.#replayGrid(index);
  }

  /**
   * Kafle otwartego panelu pokazuja sie od nowa — tak samo jak u Sleeka,
   * gdzie przelaczenie zakladki wola `showGridItems()` na jej siatce.
   * Panel byl schowany, wiec obserwator wejscia w kadr nigdy by na nim
   * nie zadzialal: element o zerowych wymiarach nie przecina niczego.
   *
   * KOLEJNOSC SKRYPTOW: `sleek-tabs.js` stoi w sekcji PRZED `sleek-reveal.js`
   * i oba sa `defer`, wiec ten kod potrafi wykonac sie ZANIM <sleek-reveal>
   * zostanie zdefiniowany. Wtedy `replay` jeszcze nie istnieje i pierwsza
   * zakladka zostalaby schowana na zawsze. Dlatego czekamy na definicje
   * elementu, zamiast sprawdzac obecnosc metody w tej klatce.
   */
  #replayGrid(index) {
    const grid = this.#panels[index]?.querySelector('sleek-reveal');
    if (!grid) return;

    customElements.whenDefined('sleek-reveal').then(() => {
      customElements.upgrade(grid);
      if (typeof (/** @type {any} */ (grid).replay) === 'function') {
        /** @type {any} */ (grid).replay();
      }
    });
  }

  #onKeydown(event, index) {
    const last = this.#tabs.length - 1;
    let next = null;

    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;

    event.preventDefault();
    this.select(next);
    this.#tabs[next].focus();
  }

  /** Edytor motywu: zaznaczenie zakładki w panelu ma ją otworzyć na stronie. */
  #onBlockSelect = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const index = this.#panels.findIndex((panel) => panel === target || panel.contains(target));
    if (index >= 0) this.select(index);
  };
}

if (!customElements.get('sleek-tabs')) customElements.define('sleek-tabs', SleekTabs);
